import { useQuery } from '@tanstack/react-query';
import { LAMBDA_FORMS } from './constants';
import authenticatedApi from './authenticatedApi';
import { notify } from '../utils/helper';
import { useEffect, useState } from 'react';

const TIME_CAP_MS = 20_000;

export enum BulkPhase {
  IndexId = '1',
  DynamoId = '2',
}

export const getDocEditFile = async (id?: string): Promise<any> => {
  if (!id) {
    return Promise.reject(new Error('Missing ID'));
  }
  try {
    const res = await authenticatedApi.get<DocEditDocumentResponse>(`${LAMBDA_FORMS}/forms/${id}`);
    return res;
  } catch (error: any) {
    let errorJson = null;
    let message = 'Failed to load document.';

    if (error.name === 'HTTPError') {
      errorJson = await error.response.json();
      const { message: errorMessage } = errorJson;
      message = errorMessage;
    }

    console.error(error);
    notify(message, 'error');

    throw errorJson;
  }
};

export const useDocEditFileQuery = (id?: string) => {
  const [timedOut, setTimedOut] = useState(false);

  // if no reedirect, back out after 20 seconds
  useEffect(() => {
    const phase = sessionStorage.getItem(`bulk:${id}`);
    if (!phase || phase !== BulkPhase.IndexId || !id) {
      setTimedOut(false);
      return;
    }

    const t = setTimeout(() => {
      setTimedOut(true);
      sessionStorage.removeItem(`bulk:${id}`);
    }, TIME_CAP_MS);

    return () => clearTimeout(t);
  }, [id]);

  const q = useQuery({
    queryKey: ['docEditFile', id],
    queryFn: () => getDocEditFile(id),
    refetchInterval: (query) => {
      if (!id) return false;
      const phase = sessionStorage.getItem(`bulk:${id}`);
      if (phase !== BulkPhase.IndexId) return false;

      const newId = query.state.data?.data?.id;
      const stillSame = !newId || newId === id;
      return stillSame ? 1000 : false;
    },
    staleTime: 180_000,
    retry: false,
  });

  const newId = q.data?.data?.id;

  // when new form id exists move bulk save flag to step 2, rules poll
  useEffect(() => {
    if (!id) return;
    const phase = sessionStorage.getItem(`bulk:${id}`);
    if (phase === BulkPhase.IndexId && newId && newId !== id) {
      sessionStorage.removeItem(`bulk:${id}`);
      sessionStorage.setItem(`bulk:${newId}`, BulkPhase.DynamoId);
    }
  }, [id, newId]);

  const isBulkSaving =
    sessionStorage.getItem(`bulk:${id}`) === BulkPhase.IndexId &&
    (!newId || newId === id) &&
    !timedOut;

  return { ...q, isBulkSaving };
};
